import { useCallback, useEffect, useMemo, useState } from "react";
import { Paginator } from "@telluscompany/design-system";
import type { SetURLSearchParams } from "react-router-dom";

import { useLocalization } from "@i18n";
import type { PaginationMeta } from "@shared/api";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type PageSize,
} from "@shared/constants/pagination.constants";
import { serializeToSearchParams } from "@shared/hooks";

export interface Gs3TablePaginatorProps {
  meta: PaginationMeta;
  useUrlState?: boolean;
  pageParamName?: string;
  limitParamName?: string;
  useSearchParamsHook?: () => [URLSearchParams, SetURLSearchParams];
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  isLoading?: boolean;
  pageSizeOptions?: readonly PageSize[];
  disabled?: boolean;
}

const computeRange = (meta: PaginationMeta) => {
  const { currentPage, itemsPerPage, totalItems } = meta;

  if (totalItems === 0) {
    return { start: 0, end: 0 };
  }

  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalItems);
  return { start, end };
};

export const Gs3TablePaginator = ({
  meta,
  useUrlState = false,
  pageParamName = "page",
  limitParamName = "limit",
  useSearchParamsHook,
  onPageChange,
  onLimitChange,
  isLoading = false,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  disabled = false,
}: Gs3TablePaginatorProps) => {
  const { translate } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParamsHook?.() ?? [
    null,
    null,
  ];
  const urlPageRaw = useUrlState ? searchParams?.get(pageParamName) : null;
  const urlPageNumber =
    urlPageRaw && !Number.isNaN(Number(urlPageRaw))
      ? Number(urlPageRaw)
      : undefined;

  const paginatorCurrentPage =
    urlPageNumber ?? meta.currentPage ?? 1;

  // While meta.totalPages is still 0 (initial fetch / F5), the DS Paginator treats
  // any page > 1 as out of range and resets the URL to page=1. Keep at least the
  // URL page valid until real meta arrives.
  const paginatorTotalPages =
    meta.totalPages > 0
      ? meta.totalPages
      : Math.max(paginatorCurrentPage, 1);

  const [goToPageInput, setGoToPageInput] = useState(
    String(paginatorCurrentPage),
  );

  useEffect(() => {
    setGoToPageInput(String(paginatorCurrentPage));
  }, [paginatorCurrentPage]);

  const { start, end } = useMemo(() => computeRange(meta), [meta]);
  const isDisabled = disabled || isLoading;
  const goToPageDisabled = isDisabled || meta.totalPages === 0;

  const navigateToPage = useCallback(
    (page: number) => {
      const clamped =
        meta.totalPages === 0
          ? Math.max(1, page)
          : Math.max(1, Math.min(page, meta.totalPages));

      if (useUrlState && setSearchParams) {
        setSearchParams((prev) =>
          serializeToSearchParams({ [pageParamName]: clamped }, prev),
        );
        return;
      }

      onPageChange?.(clamped);
    },
    [
      meta.totalPages,
      onPageChange,
      pageParamName,
      setSearchParams,
      useUrlState,
    ],
  );

  const handleLimitChange = useCallback(
    (newLimit: PageSize) => {
      if (useUrlState && setSearchParams) {
        setSearchParams((prev) =>
          serializeToSearchParams(
            { [pageParamName]: 1, [limitParamName]: newLimit },
            prev,
          ),
        );
        return;
      }

      onLimitChange?.(newLimit);
      onPageChange?.(1);
    },
    [
      limitParamName,
      onLimitChange,
      onPageChange,
      pageParamName,
      setSearchParams,
      useUrlState,
    ],
  );

  const commitGoToPage = () => {
    const parsed = Number(goToPageInput);

    if (Number.isNaN(parsed)) {
      setGoToPageInput(String(paginatorCurrentPage));
      return;
    }

    navigateToPage(parsed);
  };

  const rangeText =
    meta.totalItems === 0
      ? translate("pagination.showingEmpty")
      : translate("pagination.showingRange", {
          start,
          end,
          total: meta.totalItems,
        });

  const pageLabel = translate("pagination.pageOf", {
    current: paginatorCurrentPage,
    total: paginatorTotalPages,
  });

  return (
    <div
      data-testid="gs3-table-paginator"
      className="flex flex-wrap items-center justify-between gap-4 py-2"
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="gs3-table-paginator-rows-per-page">
            {translate("pagination.rowsPerPage")}
          </label>
          <select
            id="gs3-table-paginator-rows-per-page"
            data-testid="gs3-table-paginator-rows-per-page"
            className="rounded-md border border-muted-foreground/30 bg-background px-2 py-1 text-sm"
            value={meta.itemsPerPage ?? DEFAULT_PAGE_SIZE}
            disabled={isDisabled}
            onChange={(event) =>
              handleLimitChange(Number(event.target.value) as PageSize)
            }
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <span
          data-testid="gs3-table-paginator-range"
          className="text-sm text-muted-foreground"
        >
          {rangeText}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <span
          data-testid="gs3-table-paginator-page-label"
          className="text-sm"
        >
          {pageLabel}
        </span>

        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="gs3-table-paginator-go-to-page">
            {translate("pagination.goToPage")}
          </label>
          <input
            id="gs3-table-paginator-go-to-page"
            data-testid="gs3-table-paginator-go-to-page"
            type="number"
            min={1}
            max={Math.max(paginatorTotalPages, 1)}
            className="w-16 rounded-md border border-muted-foreground/30 bg-background px-2 py-1 text-sm"
            value={goToPageInput}
            disabled={goToPageDisabled}
            onChange={(event) => setGoToPageInput(event.target.value)}
            onBlur={commitGoToPage}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitGoToPage();
              }
            }}
            aria-label={translate("pagination.goToPageAriaLabel")}
          />
        </div>

        <Paginator
          currentPage={paginatorCurrentPage}
          totalPages={paginatorTotalPages}
          disabled={isDisabled}
          setCurrentPage={navigateToPage}
          useSearchParamsHook={useSearchParamsHook}
        />
      </div>
    </div>
  );
};
